import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { TOOLTIP_STYLE } from '../common/tooltipStyles';

import type { FC } from 'react';

export type ClipBulkAction = {
  /** Stable key; also drives the disabled-reason tooltip id. */
  key: string;
  label: string;
  variant: 'primary' | 'secondary' | 'destructive';
  disabled?: boolean;
  /** Shown on hover when `disabled`, so the state explains itself. */
  disabledReason?: string;
  onClick: () => void;
};

type Props = {
  count: number;
  /**
   * How long the selection runs, shown beside the count. Omitted when no pick
   * has a known duration (see `clipSelectionSummary`).
   */
  summary?: string;
  /**
   * Detail the count carries but does not spend bar width on — speakers, and
   * the clip/reel split of a mixed selection. Shown on hover over the count.
   */
  countTooltip?: string;
  onClear: () => void;
  /** Rendered in order. Group actions are added here, not to the view header. */
  actions: ClipBulkAction[];
};

const COUNT_TOOLTIP_ID = 'clip-bulk-count-tooltip';

/**
 * Floating bar over the Content Studio clip grid, shown while a selection
 * exists (PLAT-11049). It is a surface for group actions — Save as Reel today,
 * Delete and others later — so the actions arrive as a list rather than as
 * fixed buttons.
 *
 * Chrome deliberately matches the story-manager BulkActionBar so the two read
 * as one pattern; that one is not reused because it reads the folder-view
 * context directly.
 */
const ClipBulkActionBar: FC<Props> = ({
  count,
  summary,
  countTooltip,
  onClear,
  actions,
}) => {
  if (count === 0) return null;

  return (
    <Bar role="toolbar" aria-label="Clip actions" data-testid="clip-bulk-bar">
      <Count
        data-testid="clip-bulk-count"
        data-tooltip-id={COUNT_TOOLTIP_ID}
        data-tooltip-content={countTooltip || undefined}
      >
        {count} Selected
        {summary && (
          <Summary data-testid="clip-bulk-summary">{summary}</Summary>
        )}
      </Count>
      {countTooltip && (
        <Tooltip
          id={COUNT_TOOLTIP_ID}
          place="top"
          opacity={1}
          style={TOOLTIP_STYLE}
        />
      )}

      <GhostButton
        type="button"
        onClick={onClear}
        data-testid="clip-bulk-clear"
      >
        Clear
      </GhostButton>

      <Divider />

      {actions.map(action => {
        const Button = BUTTON_BY_VARIANT[action.variant];
        const tooltipId = `clip-bulk-${action.key}`;
        return (
          <span key={action.key}>
            <Button
              type="button"
              disabled={action.disabled}
              onClick={action.onClick}
              data-testid={tooltipId}
              data-tooltip-id={tooltipId}
              data-tooltip-content={
                action.disabled ? action.disabledReason : undefined
              }
            >
              {action.label}
            </Button>
            {action.disabled && action.disabledReason && (
              <Tooltip
                id={tooltipId}
                place="top"
                opacity={1}
                style={TOOLTIP_STYLE}
              />
            )}
          </span>
        );
      })}
    </Bar>
  );
};

export default ClipBulkActionBar;

const Bar = styled.div`
  /* Floating toolbar pinned to the bottom-centre of the view. */
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  /* Asymmetric: the label needs a wider inset than the buttons, whose own
     padding and borders already hold them off the edge. */
  padding: 6px 8px 6px 18px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--elevation-lg);
`;

/* Kept at the button row's size — a label one step up from the buttons reads as
   vertically misaligned even though it is centred, so the bar uses a single
   type size throughout (md buttons below). */
const Count = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semi-bold);
  color: var(--foreground);
  white-space: nowrap;
  padding-right: var(--spacing-xs);
`;

/* The running time is the count's other half — one uniform label rather than a
   bold count and a dimmer word, which reads as two type sizes even at one. Only
   the separator recedes. */
const Summary = styled.span`
  &::before {
    content: '·';
    color: var(--text-muted);
    font-weight: var(--font-weight-normal);
    margin: 0 var(--spacing-xs);
  }
`;

const Divider = styled.span`
  width: 1px;
  height: 16px;
  margin: 0 2px;
  background: var(--border);
  flex-shrink: 0;
`;

const buttonBase = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: var(--btn-h-md);
  padding: 0 var(--btn-px-md);
  border-radius: var(--radius-input);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  white-space: nowrap;
  cursor: pointer;
  transition:
    background var(--transition-fast) ease,
    border-color var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const GhostButton = styled.button`
  ${buttonBase}
  background: none;
  border: none;
  color: var(--text-muted);
  font-weight: var(--font-weight-medium);

  &:hover {
    background: var(--surface-elevated);
    color: var(--text-secondary);
  }
`;

/* Neutral outline: the bar already has its one Primary (Save as Reel), so
   every supporting action beside it is a Secondary. */
const SecondaryButton = styled.button`
  ${buttonBase}
  background: transparent;
  color: var(--foreground);
  border: 1px solid var(--border);
  font-weight: var(--font-weight-medium);

  &:hover:not(:disabled) {
    background: var(--surface-elevated);
    border-color: var(--text-muted);
  }
`;

const PrimaryButton = styled.button`
  ${buttonBase}
  background: var(--primary);
  color: var(--background);
  border: none;
  font-weight: var(--font-weight-semi-bold);

  &:hover:not(:disabled) {
    background: var(--primary-hover);
  }
`;

const DestructiveButton = styled.button`
  ${buttonBase}
  background: transparent;
  color: var(--destructive);
  border: 1px solid var(--destructive);
  font-weight: var(--font-weight-medium);

  &:hover:not(:disabled) {
    background: color-mix(in srgb, var(--destructive) 12%, transparent);
  }
`;

const BUTTON_BY_VARIANT: Record<
  ClipBulkAction['variant'],
  typeof PrimaryButton
> = {
  primary: PrimaryButton,
  secondary: SecondaryButton,
  destructive: DestructiveButton,
};
